import {
  requestServiceFactory,
  type APIError,
  type APIResponse,
} from '@/request'

import { authApiFactory } from './apis/auth'
import { feedbackApiFactory } from './apis/feedback'
import { githubApiFactory } from './apis/github'
import { heliosApiFactory } from './apis/helios'
import { hocuspocusApiFactory } from './apis/hocuspocus'
import { imageApiFactory } from './apis/image'
import { paymentApiFactory } from './apis/payment'
import { projectApiFactory } from './apis/project'
import { publishApiFactory } from './apis/publish'
import { signupApiFactory } from './apis/signup'
import { teamApiFactory } from './apis/team'
import { managedDocsApiFactory } from '@/apis/managed-docs'
import { managedSchemasApiFactory } from '@/apis/managed-schemas'
import { rulesApiFactory } from '@/apis/rules'
import { accessGroupsApiFactory } from '@/apis/access-groups'
import { loginPortalsApiFactory } from '@/apis/login-portals'
import { sdksApiFactory } from '@/apis/sdks'
import { themesApiFactory } from '@/apis/themes'

/** Generate a service layer for all core services */
export const apiServiceFactory = (
  /**
   * Base URL for the API. For Scalar services all services live
   * at a root path of the baseUrl
   *
   * ex. https://api.scalar.com/core/<endpointName>
   *     or
   *     http://localhost:9999/core/<endpointName>
   */
  baseUrl: string,
  /**
   * Getter function to retrieve the active auth token
   * This will be run on each request
   */
  getAuthToken: () => string,
  /**
   * For endpoints that impact which team the user is one we
   * require the ability to generate fresh tokens for the user
   *
   * Generally we will modify the appropriate records and then
   * generate a fresh set of tokens for the new teamUid
   */
  getNewTokens: (teamUid?: string) => Promise<void>,
) => {
  const requestService = requestServiceFactory(baseUrl, getAuthToken)
  return {
    auth: authApiFactory(requestService),
    team: teamApiFactory(requestService, getNewTokens),
    feedback: feedbackApiFactory(requestService),
    image: imageApiFactory(requestService),
    payment: paymentApiFactory(requestService),
    hocuspocus: hocuspocusApiFactory(requestService),
    publish: publishApiFactory(requestService),
    github: githubApiFactory(requestService),
    signup: signupApiFactory(requestService),
    project: projectApiFactory(requestService),
    managedDocs: managedDocsApiFactory(requestService),
    managedSchemas: managedSchemasApiFactory(requestService),
    rules: rulesApiFactory(requestService),
    themes: themesApiFactory(requestService),
    accessGroups: accessGroupsApiFactory(requestService),
    loginPortals: loginPortalsApiFactory(requestService),
    sdks: sdksApiFactory(requestService),
  }
}

/**
 * Service layer for helios calls
 * Uses a different endpoint and token set
 */
export const heliosServiceFactory = (
  baseUrl: string,
  getAuthToken: () => string,
) => {
  const requestService = requestServiceFactory(baseUrl, getAuthToken)
  return {
    helios: heliosApiFactory(requestService),
  }
}

export type { APIError, APIResponse }

export * from './request'
